import { gql, useQuery } from "@apollo/client";
import {
  LocationDeepNestingFragment,
  StockCountDeepNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";
import {
  StockCount,
  StockCountLineList,
} from "@towersystems/roam-common/lib/generated-types";
import { useContext } from "react";
import { STOCK_COUNT_LINES } from "./constants";
import {
  CountProductModalRootContext,
  CurrentCountContext,
  StockCountContext,
  StockCountHistoryContext,
  StockCountItemSyncContext,
  StockCountSearchContext,
  StockCountTabContext,
  StockCountSyncContext,
} from "./context";

export const useStockCountContext = () => {
  const context = useContext(StockCountContext);

  if (!context) {
    throw new Error(
      "useStockCountContext must be used within a StockCountProvider"
    );
  }

  return context;
};

export const useStockCountSyncContext = () => {
  const context = useContext(StockCountSyncContext);

  if (!context) {
    throw new Error(
      "useStockCountSyncContext must be used within a StockCountSyncProvider"
    );
  }

  return context;
};

export const useStockCountItemSyncContext = () => {
  const context = useContext(StockCountItemSyncContext);

  if (!context) {
    throw new Error(
      "useStockCountItemSyncContext must be used within a StockCountItemSyncProvider"
    );
  }

  return context;
};

export const useCurrentCountContext = () => {
  const context = useContext(CurrentCountContext);

  if (!context) {
    throw new Error(
      "useCurrentCountContext must be used within a CurrentCountProvider"
    );
  }

  return context;
};

export const useStockCountTabContext = () => {
  const context = useContext(StockCountTabContext);

  if (!context) {
    throw new Error(
      "useStockCountTabContext must be used within a StockCountTabProvider"
    );
  }

  return context;
};

export const useStockCountSearchContext = () => {
  const context = useContext(StockCountSearchContext);

  if (!context) {
    throw new Error(
      "useStockCountSearchContext must be used within a StockCountSearchProvider"
    );
  }

  return context;
};

export const useStockCountHistoryContext = () => {
  const context = useContext(StockCountHistoryContext);

  if (!context) {
    throw new Error(
      "useStockCountHistoryContext must be used within a StockCountHistoryProvider"
    );
  }

  return context;
};

export const useCountProductModalRootContext = () => {
  const context = useContext(CountProductModalRootContext);

  if (!context) {
    throw new Error(
      "useCountProductModalRootContext must be used within a CountProductModalRootProvider"
    );
  }

  return context;
};

export const useQueryStockCount = ({
  stockCountId,
}: {
  stockCountId: string;
}) => {
  const { loading, error, data, refetch } = useQuery<{
    stockCount: StockCount;
  }>(
    gql`
      ${LocationDeepNestingFragment}
      ${StockCountDeepNestingFragment}
      query queryStockCount($stockCountId: ID!) {
        stockCount(stockCountId: $stockCountId) {
          ...StockCountDeepNesting
        }
      }
    `,
    { variables: { stockCountId: stockCountId } }
  );

  return {
    stockCount: data?.stockCount,
    loading,
    error,
    refetch,
  };
};

export const useQueryStockCountLines = ({
  stockCountId,
}: {
  stockCountId: string;
}) => {
  const { loading, error, data, refetch } = useQuery<{
    stockCountLines: StockCountLineList;
  }>(STOCK_COUNT_LINES, {
    variables: {
      stockCountId: stockCountId,
    },
  });

  return {
    stockCountLines: data?.stockCountLines.items,
    loading,
    error,
    refetch,
  };
};
